<template>
    <div v-if="$store.getters['auth/isAuth'] === false">
        <div class="logo mb-5 text-center">
            <router-link :to="{ name: 'login' }">
                <img src="@/assets/lh-logo.png">
            </router-link>
        </div>

        <ValidationObserver ref="obs" v-slot="{ invalid }" tag="form" class="form-thin mb-5" @submit.prevent="submit">
            <ValidationProvider key="recaptcha" tag="div" rules="required" vid="recaptcha">
                <recaptcha ref="recaptcha" v-model="recaptchaToken" @ready="recaptchaReady" />
            </ValidationProvider>

            <div class="lead mb-4 text-center">Регистрация</div>

            <template v-if="step === 1">
                <ValidationProvider key="email" v-errors.api tag="div" class="form-group" vid="email" rules="required|email">
                    <label for="email">Почта</label>
                    <input id="email" v-model="email" v-focus type="email" class="form-control" placeholder="mail@example.com">
                </ValidationProvider>
            </template>

            <template v-if="step === 2">
                <ValidationProvider key="phone" v-errors.api tag="div" class="form-group" vid="phone" rules="required">
                    <label for="phone">Телефон</label>
                    <input id="phone" v-model="phone" v-focus type="text" class="form-control">
                </ValidationProvider>

                <small class="form-text text-muted">Код подтверждения будет отправлен вам на телефон СМС-сообщением.</small>
            </template>

            <template v-if="step === 3">
                <div class="form-group">
                    <label>Почта</label>
                    <div class="d-flex justify-content-between align-items-center">
                        <input :value="email" type="text" readonly class="form-control-plaintext">
                    </div>
                </div>

                <div class="form-group">
                    <label>Телефон</label>
                    <div class="d-flex justify-content-between align-items-center">
                        <input :value="phone" type="text" readonly class="form-control-plaintext">
                    </div>
                </div>

                <ValidationProvider key="code" v-errors tag="div" class="form-group" vid="code" rules="required">
                    <div class="d-flex justify-content-between">
                        <label for="phoneConfirmCode">Код подтверждения отправлен в СМС</label>
                        <label v-if="phoneConfirmCodeSecondsLeft">{{ phoneConfirmCodeSecondsLeft | mmss }}</label>
                    </div>

                    <input id="phoneConfirmCode" v-model="phoneConfirmCode" v-focus type="text" class="form-control">
                </ValidationProvider>
            </template>

            <template v-if="step === 4">
                <ValidationProvider key="name" v-errors.api tag="div" class="form-group" vid="name" rules="required|min:6" debounce="500">
                    <label for="name">Название компании</label>
                    <input id="name" v-model="name" v-focus type="text" class="form-control">
                </ValidationProvider>

                <ValidationProvider key="bill_currency" v-errors.api tag="div" class="form-group" vid="bill_currency" rules="required">
                    <label for="bill_currency">Валюта биллинга</label>
                    <select-currency id="bill_currency" v-model="bill_currency" type="text" class="form-control" />
                </ValidationProvider>

                <ValidationProvider key="currency" v-errors.api tag="div" class="form-group" vid="currency" rules="required">
                    <label for="currency">Валюта торговых автоматов</label>
                    <select-currency id="currency" v-model="currency" type="text" class="form-control" />
                </ValidationProvider>
            </template>

            <template v-if="step === 5">
                <div class="form-group">
                    <label class="text-normal">Почта</label>
                    <div class="d-flex justify-content-between align-items-center">
                        <input :value="email" type="text" readonly class="form-control-plaintext">
                    </div>
                </div>

                <div class="form-group">
                    <label class="text-normal">Телефон</label>
                    <div class="d-flex justify-content-between align-items-center">
                        <input :value="phone" type="text" readonly class="form-control-plaintext">
                        <label class="text-normal text-nowrap">подтвержден</label>
                    </div>
                </div>

                <ValidationProvider key="password" v-errors.api tag="div" class="form-group password-input-group" vid="password" rules="required">
                    <div class="d-flex justify-content-between">
                        <label for="password">Пароль</label>
                        <label @click="passwordVisible = !passwordVisible">показать</label>
                    </div>

                    <input id="password" v-model="password" v-focus :type="passwordVisible ? 'text' : 'password'" class="form-control">
                </ValidationProvider>

                <div class="form-group">
                    Нажимая кнопку «Зарегистрироваться», вы принимаете условия <router-link :to="{ name: 'offer' }" target="_blank">Пользовательского соглашения</router-link>.
                </div>
            </template>

            <div class="my-4">
                <button :disabled="invalid" class="btn btn-lg-192 btn-primary" type="submit">{{ lastStep ? 'Зарегистрироваться' : 'Далее' }}</button>
            </div>

            <div class="d-flex justify-content-between align-items-center">
                <button class="btn btn-link px-0" type="button" @click="back">Назад</button>
                <span>Шаг {{ step }} из 5</span>
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import axios from 'axios'
import differenceInSeconds from 'date-fns/difference_in_seconds'
import Recaptcha from '@/components/Recaptcha'

import SelectCurrency, { fetchOptions as fetchCurrencyList } from '@/components/SelectCurrency'

import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { mixinApiErrors } from '@/validation'

export default {
    components: {
        Recaptcha,
        SelectCurrency,
        ValidationObserver,
        ValidationProvider
    },

    filters: {
        mmss(seconds) {
            if (seconds <= 0) {
                return null
            }

            const sec = seconds % 60
            const min = Math.floor(seconds / 60)

            return (min < 10 ? `0${min}` : `${min}`) + ':' + (sec < 10 ? `0${sec}` : `${sec}`)
        }
    },

    data() {
        return {
            step: 1,

            email: '',
            phone: '',
            phoneConfirmCode: '',
            phoneConfirmCodeSecondsLeft: 0,
            password: '',
            passwordVisible: false,

            name: '',
            bill_currency: null,
            currency: null,

            recaptchaToken: '',
            timer: null
        }
    },

    computed: {
        lastStep() {
            return this.step === 5
        }
    },

    beforeDestroy() {
        if (this.timer) {
            clearInterval(this.timer)
        }
    },

    /**
     * По готовности компонента, сразу потрогаем капчу.
     */
    mounted() {
        this.recaptchaTouch()
    },

    methods: {
        /**
         * Колбек готовности капчи, когда она загрузилась и установилась в DOM.
         */
        recaptchaReady() {
            this.recaptchaTouch('step1_verifyEmail')
        },

        /**
         * Метод "трогает" капчу, чтобы запросить свежий токен.
         */
        recaptchaTouch(action = 'register') {
            this.recaptchaToken = ''
            this.$refs.recaptcha.execute({ action })
        },

        /**
         * Обработчик кнопки "Далее".
         */
        async submit() {
            const isValid = await this.$refs.obs.validate()

            if (!isValid) {
                return
            }

            switch (this.step) {
            case 1:
                return this.step1_verifyEmail()
            case 2:
                return this.step2_sendPhoneConfirmCode()
            case 3:
                return this.step3_verifyPhoneConfirmCode()
            case 4:
                this.step = 5
                break
            case 5:
                return this.step5_register()
            }
        },

        /**
         * Шаг 1. Проверка введенной почты на уникальность.
         */
        async step1_verifyEmail() {
            try {
                await axios.post('api/register/email-check', {
                    email: this.email
                }, {
                    headers: {
                        'X-ReCaptcha': this.recaptchaToken
                    }
                })

                this.recaptchaTouch('step2_sendPhoneConfirmCode')
                this.step = 2
            } catch (error) {
                this.recaptchaTouch('step1_verifyEmail')
                this.mixinApiErrors(error.response.data || null)
            }
        },

        /**
         * Шаг 2. Создание проверочного кода для подтверждение телефона.
         */
        async step2_sendPhoneConfirmCode() {
            try {
                const response = await axios.post('api/register/phone-confirm', {
                    phone: this.phone
                }, {
                    headers: {
                        'X-ReCaptcha': this.recaptchaToken
                    }
                })

                this.runPhoneConfirmCodeCountdown(response.data.now, response.data.expire_at)
                this.recaptchaTouch('step3_verifyPhoneConfirmCode')
                this.step = 3
            } catch (error) {
                this.recaptchaTouch('step2_sendPhoneConfirmCode')
                this.mixinApiErrors(error.response.data || null)
            }
        },

        /**
         * Шаг 3. Проверка кода
         */
        async step3_verifyPhoneConfirmCode() {
            try {
                await axios.post('api/register/phone-confirm-check', {
                    phone: this.phone,
                    code: this.phoneConfirmCode
                }, {
                    headers: {
                        'X-ReCaptcha': this.recaptchaToken
                    }
                })

                this.recaptchaTouch('step4_register')
                this.step = 4
            } catch (error) {
                this.recaptchaTouch('step3_verifyPhoneConfirmCode')
                this.mixinApiErrors(error.response.data || null)
            }
        },

        /**
         * Шаг 5. Регистрация
         */
        async step5_register() {
            try {
                await axios.post('api/register', {
                    phone: this.phone,
                    code: this.phoneConfirmCode,
                    email: this.email,
                    password: this.password,

                    name: this.name,
                    currency: this.currency,
                    bill_currency: this.bill_currency
                }, {
                    headers: {
                        'X-ReCaptcha': this.recaptchaToken
                    }
                })

                let done = await this.$store.dispatch('auth/login', {
                    username: this.email,
                    password: this.password,
                    remember: true
                })

                if (done) {
                    window.location.href = '/'
                }
            } catch (error) {
                this.recaptchaTouch('step5_register')
                this.mixinApiErrors(error.response.data || null)
            }
        },

        /**
         * Запускает "Обратный отсчет" времени жизни для кода подтверждения для телефона
         */
        runPhoneConfirmCodeCountdown(serverDateTime, phoneConfirmCodeExpireAt) {
            if (this.timer) {
                clearInterval(this.timer)
            }

            // Тут спецом 1 секунду сразу уберем, чтобы счетчик тикал не с "10:00", а "09:59".
            this.phoneConfirmCodeSecondsLeft = differenceInSeconds(phoneConfirmCodeExpireAt, serverDateTime) - 1

            this.timer = setInterval(_ => {
                const tmp = this.phoneConfirmCodeSecondsLeft - 1

                if (tmp <= 0) {
                    clearInterval(this.timer)
                    this.phoneConfirmCodeExpired()
                } else {
                    this.phoneConfirmCodeSecondsLeft = tmp
                }
            }, 1000)
        },

        phoneConfirmCodeExpired() {
            this.step = 1
            this.phoneConfirmCode = ''
            this.recaptchaTouch('step1_verifyEmail')
        },

        /**
         * Обработчик кнопки "Назад"
         */
        back() {
            if (this.step > 1) {
                this.step--
            } else {
                this.$router.push({ name: 'login' })
            }
        },

        mixinApiErrors(errors) {
            mixinApiErrors(this.$refs.obs, errors)
        }
    },

    async beforeRouterEnter(to, from, next) {
        await fetchCurrencyList()
        next()
    }
}
</script>

<style lang="scss">
    .password-input-group {
        input {
            border-right: none;
        }

        .input-group-text {
            cursor: pointer;
            background: inherit;
            border-left: none;
        }
    }
</style>

<style lang="scss" scoped>
input:read-only {
    outline: none;
}
</style>
